// PopUpSummary.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  ListItem,
  ListItemAvatar,
  Avatar,
  useTheme,
  Backdrop,
} from "@mui/material";
import {
  PopUpSummaryRoot,
  PopUpSummaryHeader,
  PopUpSummaryContent,
  PopUpSummaryFooter,
  PopUpSummaryList,
} from "./PopUpSummary.styles";
import { ShareConfigCode } from "../ShareConfigCode";
import { ContactForm } from "../ContactForm";
import CloseIcon from "@mui/icons-material/Close";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";

const currency = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
});

export const PopUpSummary = ({ onHandleClose }) => {
  const { items, subtotal } = useSelectedOptions();
  const [showContactForm, setShowContactForm] = useState(false);
  const theme = useTheme();

  const handleShowContactForm = (value) => {
    setShowContactForm(!value);
  };
  const filteredItems = items.filter(
    (item) =>
      (item?.inSummary !== false || item?.inSummary !== null) && item?.price > 0
  );

  return (
    <>
      <PopUpSummaryRoot>
        <Button
          variant="text"
          sx={{
            position: "fixed",
            zIndex: 9,
            top: 0,
            right: { xs: "-10px", md: "-5px" },
            color: theme?.palette?.companyColors?.secondary,
            fontSize: { xs: "1.75rem", lg: "2.25rem" },
            transition: "all 0.3s ease",
          }}
          onClick={() => {
            onHandleClose(false, "click");
          }}
        >
          <CloseIcon
            sx={{
              padding: "3px 0 0",
              fontSize: "3.5rem",
            }}
          />
        </Button>
        <PopUpSummaryHeader>
          <Typography variant="h3" sx={{ fontWeight: 700, flex: 1 }}>
            Your Configuration
          </Typography>
        </PopUpSummaryHeader>
        <PopUpSummaryContent dense>
          <Box
            sx={{
              position: "relative",
              width: { xs: "100%", lg: "auto" },
              flex: 1.3,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              padding: { xs: "1rem 0", lg: "1rem 2rem 1rem 0" },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                flex: 1,
                mb: "1.5rem",
                fontSize: "1.7rem",
              }}
            >
              Selected Options
            </Typography>
            <PopUpSummaryList>
              {filteredItems.map((it, indexIt) => {
                return (
                  <React.Fragment key={it.groupKey}>
                    <ListItem>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flex: 1,
                          padding: "0.25rem 0.5rem",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            textAlign: "right",
                            flex: 1,
                          }}
                        >
                          {it.image ? (
                            <ListItemAvatar>
                              <Avatar
                                variant="square"
                                src={it.image}
                                alt={it.name}
                                sx={{
                                  borderRadius: theme?.borderRadius?.small,
                                  boxShadow: "0 0 2px 0 rgba(0,0,0,0.25)",
                                }}
                              />
                            </ListItemAvatar>
                          ) : null}
                          {it.subCategory && (
                            <Typography
                              variant="body1"
                              sx={{ fontSize: "1.4rem", textAlign: "left" }}
                            >
                              {it.subCategory}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            justifyContent: "flex-end",
                            flex: 1,
                            margin: it.name ? "5px 0 -1px" : 0,
                          }}
                        >
                          {it.name && (
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 500,
                                lineHeight: 1,
                                fontSize: "1.35em",
                                textAlign: "right",
                              }}
                            >
                              {it.name}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: {
                                xs: "1.1rem",
                                md: "1.3rem",
                              },
                            }}
                            color={
                              it.name
                                ? theme.palette.companyColors.greyLight1
                                : theme.palette.companyColors.secondary
                            }
                          >
                            {currency.format(Number(it.price) || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                    {filteredItems.length !== indexIt + 1 && (
                      <Divider
                        component="li"
                        sx={{
                          width: "100%",
                          margin: "auto",
                          borderColor: theme.palette.companyColors.greyLight1,
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </PopUpSummaryList>
            <Box
              sx={{
                width: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: { xs: "center", lg: "flex-end" },
                gap: "10px",
                mt: "20px",
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 500 }}>
                Total:
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-1px",
                  fontSize: { xs: "2.5rem", md: "3rem", lg: "3.5rem" },
                  lineHeight: 1,
                }}
              >
                {currency.format(subtotal)}*
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              padding: { xs: "2rem 0 0", lg: "1rem 4rem" },
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: "4rem",
              "&:after": {
                content: "''",
                display: "block",
                width: "100%",
                height: "1px",
                position: "absolute",
                top: 0,
                left: 0,
                background: theme?.palette?.companyColors?.greyLight1,
                [theme.breakpoints.up("lg")]: {
                  width: "1px",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                },
              },
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  flex: 1,
                  mb: "1.5rem",
                  fontSize: { xs: "2.25rem", lg: "2rem" },
                }}
              >
                Your WYDST R8S configuration is ready to share with others.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 400,
                  flex: 1,
                  fontSize: { xs: "1.6rem", lg: "1.4rem" },
                  mb: "1rem",
                }}
              >
                Copy the link to your clipboard or share it directly.
              </Typography>
              <ShareConfigCode />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  flex: 1,
                  mb: "1.5rem",
                  fontSize: { xs: "2.25rem", lg: "2rem" },
                  alignSelf: "flex-start",
                }}
              >
                Submit interest
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 400,
                  flex: 1,
                  fontSize: { xs: "1.6rem", lg: "1.4rem" },
                }}
              >
                This body kit is in the market test phase. We’re gauging
                customer interest and talking with partners to bring it to life,
                so the listed price is only indicative and may change after
                development.
                <br />
                <br />
                By submitting your interest, you’ll help us measure demand and
                refine costs. It’s non-binding, but please submit only if you’re
                genuinely interested in modifying your R8, and include your car
                details. We’ll contact you with updates.
              </Typography>
              <Button
                sx={{
                  color: theme?.palette?.companyColors?.neutral,
                  background: theme?.palette?.companyColors?.primary,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: theme?.borderRadius?.small,
                  padding: { xs: "0.75rem 5rem", xl: "1rem 4rem" },
                  width: "100%",
                  mt: "3rem",
                }}
                onClick={() => {
                  handleShowContactForm(showContactForm);
                }}
              >
                <Typography
                  variant="button"
                  sx={{
                    textTransform: "initial",
                    fontSize: { xs: "1.85rem", lg: "1.75rem" },
                    fontWeight: 400,
                    textAlign: "center",
                  }}
                >
                  Submit interest
                </Typography>
              </Button>
            </Box>
          </Box>
        </PopUpSummaryContent>

        <PopUpSummaryFooter>
          <Typography
            variant="body1"
            sx={{
              width: "100%",
              textAlign: "center",
              fontSize: "1.2rem",
            }}
          >
            <b>*Disclaimer:</b> Prices shown in this configurator are
            illustrative only and meant to indicate potential costs based on
            selected options.
          </Typography>
        </PopUpSummaryFooter>
      </PopUpSummaryRoot>
      <Backdrop
        open={showContactForm}
        sx={{
          color: theme?.palette?.companyColors?.secondary,
          zIndex: (theme) => theme.zIndex.modal + 2,
          backdropFilter: `blur(2px)`,
          background: `rgba(${theme?.palette?.companyColors?.secondaryRGB},0.75)`,
        }}
      >
        <ContactForm
          onClose={handleShowContactForm}
          showContactForm={showContactForm}
        />
      </Backdrop>
    </>
  );
};
